import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebase'; // Firebase Firestore import
import { collection, query, where, updateDoc, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth'; // Importer signOut
import { auth } from '../../firebase';
import './ServerSchedule.css';
import { 
  FaCalendarAlt, 
  FaClock, 
  FaUser, 
  FaSpinner,
  FaExclamationTriangle
} from 'react-icons/fa';

const ServerSchedule = () => {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState(null); // État pour stocker l'emploi du temps de l'utilisateur
  const [totalHours, setTotalHours] = useState(0); // Total des heures travaillées
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weeklyTarget] = useState(35); // Objectif hebdomadaire en heures
  
  // Vérifier si l'utilisateur est connecté
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
      } else {
        navigate('/login'); // Rediriger vers la page de connexion si non connecté
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const formatHours = (hours) => {
    const fullHours = Math.floor(hours); // Partie entière des heures
    const minutes = Math.round((hours - fullHours) * 60); // Partie décimale convertie en minutes
    return `${fullHours}h${minutes.toString().padStart(2, '0')}`; // Format h:mm avec zéro-padding
  };

  // Calculer les heures hebdomadaires et le pourcentage
  const calculateWeeklyStats = () => {
    if (!schedule) return { weeklyHours: 0, percentage: 0 };

    const daysInWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    let weeklyHours = 0;

    daysInWeek.forEach((day) => {
      const daySchedule = schedule[day];
      if (daySchedule && daySchedule !== "Ne travaille pas") {
        const periods = daySchedule.split(" / ");
        periods.forEach((period) => {
          const [start, end] = period.split(" - ");
          const startTime = new Date(`2023-12-01T${start}:00`);
          const endTime = new Date(`2023-12-01T${end}:00`);
          const hours = (endTime - startTime) / 1000 / 60 / 60;
          weeklyHours += hours;
        });
      }
    });

    const percentage = Math.min((weeklyHours / weeklyTarget) * 100, 100);
    return { weeklyHours, percentage };
  };

  // Récupérer l'emploi du temps de l'utilisateur connecté
   useEffect(() => {
    const fetchSchedule = async () => {
      if (user) {
        try {
          setLoading(true);
          setError(null);
          const scheduleRef = collection(db, 'horaires');
          const q = query(scheduleRef, where('employeeId', '==', user.uid));
          const querySnapshot = await getDocs(q);
  
          querySnapshot.forEach((doc) => {
            setSchedule(doc.data().schedule); // Extraire l'emploi du temps
          });
        } catch (error) {
          console.error("Erreur lors de la récupération de l'emploi du temps :", error);
          setError("Erreur lors du chargement de l'emploi du temps");
        } finally {
          setLoading(false);
        }
      }
    };
  
    fetchSchedule();
  }, [user]);

  const calculateTotalHours = useCallback(async () => {
    if (!schedule || !user) return;
  
    const currentDate = new Date();
    const currentDay = currentDate.getDate(); // Jour actuel du mois
  
    const daysInWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const hoursWorked = [];
  
    // On va vérifier chaque jour de la semaine jusqu'au jour actuel du mois
    for (let i = 0; i < currentDay; i++) {
      const dayOfWeek = daysInWeek[i % 7]; // Calculer le jour de la semaine pour la date
      const daySchedule = schedule[dayOfWeek];
  
      if (daySchedule && daySchedule !== "Ne travaille pas") {
        const periods = daySchedule.split(" / "); // Diviser les périodes par "/"
        let dailyHours = 0;
      
        periods.forEach((period) => {
          const [start, end] = period.split(" - ");
          const startTime = new Date(`2023-12-01T${start}:00`);
          const endTime = new Date(`2023-12-01T${end}:00`);
          const hours = (endTime - startTime) / 1000 / 60 / 60; // Conversion en heures
          dailyHours += hours;
        });
      
        hoursWorked.push(dailyHours); // Ajouter les heures du jour au tableau
      }      
    }
  
    let total = hoursWorked.reduce((acc, hours) => acc + hours, 0); // Calcul du total
  
    // Ajouter les heures supplémentaires depuis la base de données
    try {
      const scheduleRef = collection(db, 'horaires');
      const q = query(scheduleRef, where('employeeId', '==', user.uid));
      const querySnapshot = await getDocs(q);
  
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.heure_supp) {
          total += parseFloat(data.heure_supp);
        }
  
        // Mettre à jour le total dans la base de données
        updateDoc(doc.ref, { total_hours: total });
      });
  
      setTotalHours(total);
    } catch (error) {
      console.error("Erreur lors de la récupération/mise à jour des heures supplémentaires :", error);
    }
  }, [schedule, user]); // `calculateTotalHours` dépend de `schedule` et `user`  

  useEffect(() => {
    calculateTotalHours(); // Appeler le calcul des heures dès que l'emploi du temps est récupéré
  }, [schedule, calculateTotalHours]);

  const renderSchedule = () => {
    if (!schedule) return null;

    const daysInFrench = {
      Sunday: "DIMANCHE",
      Monday: "LUNDI",
      Tuesday: "MARDI",
      Wednesday: "MERCREDI",
      Thursday: "JEUDI",
      Friday: "VENDREDI",
      Saturday: "SAMEDI"
    };

    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    return (
      <div className="server-schedule-table-container">
        <table className="server-schedule-table">
          <thead>
            <tr>
              {dayNames.map((day) => (
                <th key={day} className="server-schedule-table-header">{daysInFrench[day]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {dayNames.map((day) => (
                <td 
                  key={day} 
                  className={`server-schedule-table-cell ${
                    schedule[day] === 'Ne travaille pas' 
                      ? 'server-schedule-day-off' 
                      : 'server-schedule-day-work'
                  }`}
                >
                  <span className={
                    schedule[day] === 'Ne travaille pas' 
                      ? 'server-schedule-day-off-text' 
                      : 'server-schedule-day-work-text'
                  }>
                    {schedule[day] === 'Ne travaille pas' ? 'Ne travaille pas' : schedule[day]}
                  </span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="server-schedule-container">
        <div className="server-schedule-loading-container">
          <FaSpinner className="server-schedule-loading-spinner" />
          <p className="server-schedule-loading-text">Chargement de l'emploi du temps...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="server-schedule-container">
        <div className="server-schedule-error-container">
          <FaExclamationTriangle className="server-schedule-error-icon" />
          <h3 className="server-schedule-error-title">Erreur</h3>
          <p className="server-schedule-error-message">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="server-schedule-container">
      {/* Header */}
      <div className="server-schedule-header">
        <div className="server-schedule-header-content">
          <div className="server-schedule-title-section">
            <div className="server-schedule-title-icon">
              <FaCalendarAlt />
            </div>
            <div className="server-schedule-title-text">
              <h1 className="server-schedule-title">Interface Serveur</h1>
              <p className="server-schedule-subtitle">Emploi du temps</p>
            </div>
          </div>
          
          {user && (
            <div className="server-schedule-user-info">
              <FaUser className="server-schedule-user-icon" />
              <span className="server-schedule-user-name">{user.email}</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="server-schedule-main-content">
        {/* Schedule Section */}
        <div className="server-schedule-section">
          <div className="server-schedule-section-header">
            <h2 className="server-schedule-section-title">
              <FaCalendarAlt className="server-schedule-section-icon" />
              Planning hebdomadaire
            </h2>
          </div>
          <div className="server-schedule-section-content">
            {renderSchedule()}
          </div>
        </div>

        {/* Hours Section */}
        <div className="server-schedule-hours-card">
          <div className="server-schedule-hours-content">
            <div className="server-schedule-hours-icon">
              <FaClock />
            </div>
            <div className="server-schedule-hours-text">
              <p className="server-schedule-hours-label">Total des heures travaillées ce mois</p>
              <p className="server-schedule-hours-value">{formatHours(totalHours)}</p>
            </div>
          </div>
          
          {/* Progress Section */}
          <div className="server-schedule-hours-progress">
            <div className="server-schedule-progress-bar">
              <div 
                className="server-schedule-progress-fill" 
                style={{ width: `${calculateWeeklyStats().percentage}%` }}
              ></div>
            </div>
            <p className="server-schedule-progress-text">
              {formatHours(calculateWeeklyStats().weeklyHours)} / {weeklyTarget}h par semaine 
              ({Math.round(calculateWeeklyStats().percentage)}%)
            </p>
          </div>
        </div>
      </div>
    </div>
  );  
};

export default ServerSchedule;